import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
}

export default function Pagination({ currentPage, totalPages, onPageChange, className="" }: PaginationProps) {
  const generatePageNumbers = () => {
    const pages: (number | string)[] = []
    const showPages = 7

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages - 1)
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 3) {
        pages.push(1)
        pages.push(2)
        pages.push('...')
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <hr className="w-full border-gray-300" />
      <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-4 px-2">
        {/* Page info - hidden on mobile if space is tight */}
        <div className="flex items-center gap-4 order-2 sm:order-1">
          <span className="text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
            Page {currentPage} of {totalPages}
          </span>
          
          {/* Page numbers - hidden on mobile */}
          <div className="hidden sm:flex gap-1">
            {generatePageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === 'number' && onPageChange(page)}
                disabled={page === '...' || page === currentPage}
                className={`w-8 h-8 rounded-md text-sm font-normal flex items-center justify-center transition-colors ${
                  page === currentPage
                    ? 'bg-white border border-wine-100 text-gray-900 font-medium'
                    : page === '...'
                    ? 'bg-white text-gray-400 cursor-default'
                    : 'bg-white text-gray-400 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation arrows */}
        <div className="flex rounded-md overflow-hidden order-1 sm:order-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`p-2 sm:p-3 bg-wine-200 transition-colors ${
              currentPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-wine-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </button>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`p-2 sm:p-3 bg-wine-100 transition-colors ${
              currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-wine-200'
            }`}
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </button>
        </div>
      </div>
    </div>
  )
}